import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppSidebar from '@/Components/layout/AppSidebar';
import AppHeader from '@/Components/layout/AppHeader';
import AppTopNav from '@/Components/layout/AppTopNav';
import NavChrome from '@/Components/layout/NavChrome';
import { LayoutPreferencesProvider, useLayoutPreferences } from '@/contexts/LayoutPreferencesContext';
import { useIsMobileShell } from '@/hooks/useMediaQuery';
import { cn } from '@/Components/ui/utils';

export default function AppLayout() {
  const { user } = useAuth();
  const userId = user?.id ?? 'guest';
  const menuPosition = user?.company?.menu_position;
  const defaultMode = menuPosition === 'top' || menuPosition === 'superior' ? 'top' : 'sidebar';
  return <LayoutPreferencesProvider key={userId} userId={userId} defaultMode={defaultMode}>
    <AppShell />
  </LayoutPreferencesProvider>;
}

function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobileShell();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { preferences } = useLayoutPreferences();
  const useDesktopTopNav = preferences.mode === 'top' && !isMobile;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobile, mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const shellClass = cn(
    'nx-shell',
    isMobile && 'nx-shell--mobile',
    !isMobile && !useDesktopTopNav && collapsed && 'nx-shell--collapsed',
    useDesktopTopNav && 'nx-shell--top-nav',
    isMobile && mobileOpen && 'nx-shell--mobile-open',
  );

  return (
    <div className={shellClass}>
      {isMobile ? (
        <button
          type="button"
          className={cn('nx-sidebar-overlay', mobileOpen && 'nx-sidebar-overlay--open')}
          aria-label="Fechar menu"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {!useDesktopTopNav && <AppSidebar
        collapsed={isMobile ? false : collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onToggleMobile={() => setMobileOpen((v) => !v)}
      />}

      <div className="nx-main">
        <AppHeader
          user={user}
          onLogout={handleLogout}
          showMenuButton={isMobile}
          menuOpen={mobileOpen}
          onMenuClick={() => setMobileOpen((v) => !v)}
          topNav={useDesktopTopNav ? <AppTopNav /> : undefined}
        />
        <NavChrome />
        <div className="nx-content-stage">
          <main className="nx-content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
